import { apiRequest, getAccessToken } from './apiClient';
import { Capacitor } from '@capacitor/core';
import { isAndroidRestrictedModule, isAndroidRestrictedPage } from '../utils/platformFeatures';
import { getRuntimeVersionLabel } from './runtimeAppInfo';

const PAGE_TO_MODULE = {
  alisveris: 'shopping', shopping: 'shopping',
  takvim: 'calendar', calendar: 'calendar', ozelTakvimim: 'calendar',
  finans: 'finance', finance: 'finance',
  gorev: 'tasks', gorevler: 'tasks', tasks: 'tasks',
  harcama: 'expenses', harcamalar: 'expenses', expenses: 'expenses', sharedExpenses: 'expenses',
  saglik: 'health', health: 'health',
  harita: 'map', map: 'map', aileHaritasi: 'map',
  cocuk: 'children', children: 'children', cocukTakibi: 'children',
  chat: 'chat', aileSohbeti: 'chat',
  belgeler: 'documents', documents: 'documents',
  assistant: 'assistant', aileAsistani: 'assistant',
  nepisirsem: 'cooking', nePisirsam: 'cooking', cooking: 'cooking',
  aile: 'family_setup', ailem: 'family_setup'
};

const normalizeLang = (lang) => (lang === 'en' ? 'en' : 'tr');
const isNative = () => {
  try { return !!window.Capacitor?.isNativePlatform?.(); } catch { return false; }
};
const getPlatform = () => {
  try { return Capacitor.getPlatform(); } catch { return isNative() ? 'ios' : 'web'; }
};

function fireAndForget(promise) {
  Promise.resolve(promise).catch((err) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[engagement]', err?.message || err);
    }
  });
}

export function moduleFromPage(pageKey) {
  return PAGE_TO_MODULE[pageKey] || null;
}

export async function trackEngagementPageView({ familyId, pageKey, moduleKey, language, metadata } = {}) {
  const resolvedModule = moduleKey || moduleFromPage(pageKey);
  if (isAndroidRestrictedPage(pageKey) || isAndroidRestrictedModule(resolvedModule)) return;
  if (!getAccessToken()) return;
  if (!pageKey && !resolvedModule) return;
  const appVersion = await getRuntimeVersionLabel();
  fireAndForget(apiRequest('/engagement/page-view', {
    method: 'POST',
    body: JSON.stringify({
      familyId: familyId || null,
      pageKey: pageKey || null,
      moduleKey: resolvedModule,
      language: normalizeLang(language),
      platform: getPlatform(),
      appVersion,
      metadata: metadata || {}
    })
  }));
}

export function trackEngagementDataAdded({ familyId, moduleKey, language, itemId, metadata } = {}) {
  if (isAndroidRestrictedModule(moduleKey)) return;
  if (!getAccessToken()) return;
  if (!familyId || !moduleKey) return;
  fireAndForget(apiRequest('/engagement/data-added', {
    method: 'POST',
    body: JSON.stringify({
      familyId,
      moduleKey,
      language: normalizeLang(language),
      itemId: itemId || null,
      metadata: metadata || {}
    })
  }));
}

export async function trackPushPermissionStatus({ familyId, status, language, metadata } = {}) {
  if (!getAccessToken()) return;
  const appVersion = await getRuntimeVersionLabel();
  fireAndForget(apiRequest('/engagement/push-permission', {
    method: 'POST',
    body: JSON.stringify({
      familyId: familyId || null,
      status: status || 'unknown',
      language: normalizeLang(language),
      platform: getPlatform(),
      appVersion,
      metadata: metadata || {}
    })
  }));
}

export async function getEngagementAdminConfig() {
  const res = await apiRequest('/engagement/admin/config');
  return res.config;
}

export async function saveEngagementAdminConfig(config) {
  const res = await apiRequest('/engagement/admin/config', {
    method: 'PUT',
    body: JSON.stringify({ config })
  });
  return res.config;
}

export async function resetEngagementAdminConfig() {
  const res = await apiRequest('/engagement/admin/config/reset-defaults', {
    method: 'POST',
    body: JSON.stringify({})
  });
  return res.config;
}

export async function getEngagementAdminSummary() {
  const res = await apiRequest('/engagement/admin/summary');
  return res.summary;
}

export async function getAppReviewAdminConfig() {
  const res = await apiRequest('/engagement/admin/app-review/config');
  return res.config;
}

export async function saveAppReviewAdminConfig(config) {
  const res = await apiRequest('/engagement/admin/app-review/config', {
    method: 'PUT',
    body: JSON.stringify({ config })
  });
  return res.config;
}

export async function getAppReviewAdminSummary(days = 30) {
  const safeDays = Math.min(Math.max(Number(days || 30), 1), 365);
  const res = await apiRequest(`/engagement/admin/app-review/summary?days=${safeDays}`);
  return res.summary;
}

export async function getEngagementDeliveryHistory({
  windowKey = '24h',
  status = '',
  moduleKey = '',
  segment = '',
  source = '',
  search = '',
  limit = 100,
} = {}) {
  const params = new URLSearchParams({
    window: windowKey,
    limit: String(Math.min(Math.max(Number(limit || 100), 1), 100)),
  });
  if (status) params.set('status', status);
  if (moduleKey) params.set('module', moduleKey);
  if (segment) params.set('segment', segment);
  if (source) params.set('source', source);
  if (search) params.set('search', search);
  const res = await apiRequest('/engagement/admin/delivery-history?' + params.toString());
  return res.history;
}

export async function getLiveUserActivityOverview(windowKey = 'live') {
  const allowed = new Set(['live', '1', '3', '6', '12', '24']);
  const selected = allowed.has(String(windowKey)) ? String(windowKey) : 'live';
  const res = await apiRequest(`/engagement/admin/activity?window=${encodeURIComponent(selected)}`);
  return res.overview;
}

export async function getAdminOperationsOverview() {
  const res = await apiRequest('/engagement/admin/operations-overview');
  return res.overview;
}


export async function runEngagementAdminProcess({ dryRun = true, limit = 25 } = {}) {
  return apiRequest('/engagement/admin/process', {
    method: 'POST',
    body: JSON.stringify({ dryRun, limit })
  });
}


export async function testEngagementAdminNotification() {
  return apiRequest('/engagement/admin/test', {
    method: 'POST',
    body: JSON.stringify({})
  });
}
